import { PsCalculationResultsDataItemState, } from "store/ps-workspace/ps-workspace.types";
import { getLineColor, sortByResponseId, prepareLegendDataItems, getHeatMapColorScale, } from "ps-components/Plot/utils";
import { getArrayMinMax } from "utils/data";
import * as fonts from "styles/fontValues.module.scss";
const baseDataConfig = {
    type: "contour",
    showscale: false,
    connectgaps: false,
    autocontour: false,
    showlegend: true,
    zsmooth: "best",
    line: {
        width: 1.25,
    },
};
const defaultEmptyData = {
    ...baseDataConfig,
    x: [null],
    y: [null],
    z: [[null]],
    type: "scatter",
};
const defaultZGranularity = 10;
const getXY = (plotData, key) => plotData.data.reduce((arr, i) => [...arr, ...i.map((i) => i[key])].filter((val, i, self) => val !== null && self.indexOf(val) === i), []);
const mapZValues = (zValues) => {
    const maxZAxisArrLength = getArrayMinMax(zValues.map((i) => i.length)).max;
    const newZAxisArrays = Array.from(Array(maxZAxisArrLength), () => []);
    zValues.forEach((zAxisArr) => zAxisArr.forEach((z, index) => newZAxisArrays[index].push(z)));
    return newZAxisArrays;
};
const prepareInitialPlotData = ({ responses }, zDictionaryParameters, analiseOptions, configurationOptions) => {
    const legendDataItems = prepareLegendDataItems(responses, zDictionaryParameters, analiseOptions, configurationOptions);
    if (responses.length) {
        return responses
            .slice()
            .sort(sortByResponseId)
            .map(({ id, maxColor }) => ({
            ...defaultEmptyData,
            responseId: id,
            colorscale: [
                [0, "rgba(255, 255, 255, 0)"],
                [1, "rgba(255, 255, 255, 0)"],
            ],
            line: {
                color: getLineColor(id, maxColor?.hexValue),
            },
            legendData: legendDataItems[id] ?? {},
        }));
    }
    else {
        return [defaultEmptyData];
    }
};
const preparePlotData = (prevState, storedLineGraphContours, { responses }, zDictionaryParameters) => {
    const preparedData = storedLineGraphContours.reduce((arr, resultItem) => {
        const { responseId } = resultItem;
        const resultItemData = resultItem?.data ?? [];
        const stateText = resultItem?.dataTopState ?? "Undefined results";
        const stateMessage = resultItem.dataTopStateMessage;
        const zContoursLength = resultItemData.reduce((arr, item) => {
            item.map(({ zWithConfidence = [] }) => arr.push(zWithConfidence.length));
            return arr;
        }, []);
        const maxZContoursLength = zContoursLength.length ? getArrayMinMax(zContoursLength).max : 1;
        const zContours = Array.from(Array(maxZContoursLength), () => []);
        resultItemData.forEach((item, itemIndex) => {
            item.forEach(({ zWithConfidence = [], state }) => {
                Array.from(Array(maxZContoursLength)).forEach((_, index) => {
                    const z = zWithConfidence[index] ? (zWithConfidence[index].z ?? state) : state;
                    zContours[index][itemIndex]
                        ? zContours[index][itemIndex].push(z)
                        : zContours[index].splice(itemIndex, 0, [z]);
                });
            });
        });
        const isLoading = resultItemData.some((i) => i.some(({ state }) => state === PsCalculationResultsDataItemState.Pending));
        const isWarningStatus = [
            PsCalculationResultsDataItemState.Conflict,
            PsCalculationResultsDataItemState.IncompleteDependencySet,
            PsCalculationResultsDataItemState.NoOptimum,
            PsCalculationResultsDataItemState.TooManyDegreesOfFreedom,
            PsCalculationResultsDataItemState.Undefined,
        ].includes(resultItem.dataTopState);
        const { zParameterId, zUnitsId, showLines, minColor, maxColor, opacity, granularity, visible } = responses.find(({ id }) => responseId === id) ?? {};
        const zAxisUnit = zParameterId && zUnitsId
            ? (zDictionaryParameters[zParameterId]?.units.find(({ id }) => id === zUnitsId)?.name ?? "")
            : "";
        const surfaceOpacity = isLoading ? (opacity ?? 0.5) / 2 : (opacity ?? 0.5);
        const lineColor = getLineColor(responseId, maxColor?.hexValue, isLoading);
        const [minSurfaceColor, maxSurfaceColor] = getHeatMapColorScale(responseId, minColor?.hexValue, maxColor?.hexValue, surfaceOpacity);
        let zContoursPlotData = [];
        if (!zContours.length) {
            const prevStateItem = prevState.find(({ responseId: id }) => responseId === id);
            if (prevStateItem) {
                const { line, z } = { ...prevStateItem };
                zContoursPlotData = [
                    {
                        ...prevStateItem,
                        isLoading,
                        ...(isLoading ? { z } : isWarningStatus ? { warning: true } : { z: [[null]], warning: true }),
                        stateText,
                        stateMessage,
                        colorscale: [
                            [0, minSurfaceColor],
                            [1, maxSurfaceColor],
                        ],
                        visible: visible || "legendonly",
                        hovertemplate: `${zAxisUnit}<extra></extra>`,
                        line: { ...line, color: lineColor },
                    },
                ];
            }
            else {
                zContoursPlotData = [
                    {
                        ...defaultEmptyData,
                        colorscale: [
                            [0, minSurfaceColor],
                            [1, maxSurfaceColor],
                        ],
                        responseId,
                        isLoading,
                        stateText,
                        stateMessage,
                        ...(isWarningStatus && { warning: true }),
                        visible: visible || "legendonly",
                        hovertemplate: `${zAxisUnit}<extra></extra>`,
                        line: { width: 1.25, color: lineColor },
                    },
                ];
            }
        }
        else {
            const getZMinMax = (zValues, key) => {
                const allZ = zValues.reduce((arr, i) => [...arr, ...i.filter((i) => typeof i === "number" && !isNaN(i))], []);
                return getArrayMinMax(allZ)[key];
            };
            zContoursPlotData = zContours.map((zValues, index) => ({
                ...baseDataConfig,
                x: getXY(resultItem, "x"),
                y: getXY(resultItem, "y"),
                z: mapZValues(zValues),
                contours: {
                    coloring: "fill",
                    showlines: !!showLines,
                    showlabels: !!showLines,
                    labelfont: {
                        family: fonts.roboto,
                    },
                    start: getZMinMax(zValues, "min"),
                    end: getZMinMax(zValues, "max"),
                    size: getZMinMax(zValues, "max") / Number(granularity ?? defaultZGranularity),
                },
                colorscale: [
                    [0, minSurfaceColor],
                    [1, maxSurfaceColor],
                ],
                line: {
                    width: 1.25,
                    color: lineColor,
                    ...(!!index && { dash: "dot" }),
                },
                isAdditionalLine: !!index,
                responseId,
                isLoading,
                visible: visible || "legendonly",
                hovertemplate: `${zAxisUnit}<extra></extra>`,
            }));
        }
        return [...arr, ...zContoursPlotData];
    }, []);
    const preparedContoursIds = preparedData
        .map(({ responseId }) => responseId)
        .filter((val, index, self) => val !== undefined && self.indexOf(val) === index);
    const responsesIds = responses.map(({ id }) => id);
    const filteredState = prevState.filter(({ responseId: id }) => id && !preparedContoursIds.includes(id) && responsesIds.includes(id));
    const preparedPlotData = [...filteredState, ...preparedData].slice().sort(sortByResponseId);
    return preparedPlotData.length ? preparedPlotData : [defaultEmptyData];
};
export { preparePlotData, prepareInitialPlotData };
